import { BaseSuccessResponse, PaginatedData } from "../../response";
import type { ActSlug } from "../acts";
import type { FeatureKey } from "../features";

/*
 * WhatsApp messaging resold from Sparrow (claude/design/whatsapp-messaging/).
 * The contract is 02-architecture.md §11, frozen for the parallel build:
 *
 *   §11.1 control panel  `/api/whatsapp-platform/*`  (cpanel)
 *   §11.2 SageMIN        `/api/whatsapp/*`           (admin)
 *   §11.3 customer       `/api/whatsapp/me/` and new optional fields on
 *                        payment-initiate, food checkout and the member OTP
 *
 * Money rule: amounts are NPR decimal strings with two decimals (`"1.50"`),
 * made by the server from integer paisa, and shown with `formatNpr`. There is
 * never a dollar amount. Times are ISO 8601 UTC; months are `YYYY-MM` in
 * Asia/Kathmandu. Numbers are masked in cpanel (`98•••••678`) and full in
 * SageMIN.
 *
 * Bodies arrive in amphitheater's `{data, message, status_code}` envelope,
 * which the services also accept bare. Lists that page carry the repo's
 * pagination block inside `data` (`PaginatedData`).
 */

/** An NPR amount as a decimal string with two decimals, e.g. `"1.50"`. Never a float. */
export type WhatsAppNpr = string;

/** Shared = Picovico's number (billed); own = the company's number (never billed). */
export type WhatsAppMode = "shared" | "own";

/** Why a message is sent; each has one template per account (except campaign). */
export type WhatsAppPurpose =
  | "ticket"
  | "ticket_food"
  | "food"
  | "reservation"
  | "otp"
  | "campaign";

/** A ledger row's purpose: a template purpose, or `test` for "Send test". */
export type WhatsAppMessagePurpose = WhatsAppPurpose | "test";

/** Meta's pricing class, derived from the purpose (02 §4). */
export type WhatsAppCategory = "utility" | "authentication" | "marketing";

/** What sits above a template's text. Nothing sends `document` in v1. */
export type WhatsAppHeader = "none" | "image" | "document";

/** 02 §6. Billable = shared mode and sent, delivered or read. */
export type WhatsAppMessageStatus =
  | "queued"
  | "sending"
  | "accepted"
  | "sent"
  | "delivered"
  | "read"
  | "failed"
  | "rejected"
  | "skipped"
  | "unconfirmed";

/** What a ledger row was made for. */
export type WhatsAppSourceType =
  | "user_ticket"
  | "order"
  | "booking"
  | "otp"
  | "campaign"
  | "test"
  | "resend";

export type WhatsAppStatementStatus = "draft" | "final" | "invoiced" | "paid";

/** `hall` lines are a breakdown and are not added to the total. */
export type WhatsAppStatementLineKind = "category" | "adjustment" | "hall";

export type WhatsAppCampaignStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "sent"
  | "cancelled"
  | "failed";

export type WhatsAppOptInSource = "checkout" | "profile" | "import";
export type WhatsAppOptOutSource = "profile" | "admin" | "stop_reply";
export type WhatsAppExportFormat = "csv" | "pdf";
export type WhatsAppOtpChannel = "whatsapp" | "sms";

/* ------------------------------------------------------------- constants */

/** Every purpose a template can have, in the order the editors offer them. */
export const WHATSAPP_PURPOSES: readonly WhatsAppPurpose[] = [
  "ticket",
  "ticket_food",
  "food",
  "reservation",
  "otp",
  "campaign",
];

/**
 * The purposes the shared (Picovico) number may send: utility and
 * authentication only, never marketing (99 D3). The control panel's template
 * editor offers these; SageMIN's own-number editor offers every purpose.
 */
export const WHATSAPP_SHARED_PURPOSES: readonly WhatsAppPurpose[] = [
  "ticket",
  "ticket_food",
  "food",
  "reservation",
  "otp",
];

/** 02 §4: a template's category is derived from its purpose. */
export const WHATSAPP_PURPOSE_CATEGORY: Readonly<
  Record<WhatsAppPurpose, WhatsAppCategory>
> = {
  ticket: "utility",
  ticket_food: "utility",
  food: "utility",
  reservation: "utility",
  otp: "authentication",
  campaign: "marketing",
};

/** The `whatsapp` sub-flags by the name the API's `features` maps use. */
export const WHATSAPP_SUB_FEATURES = {
  tickets: "whatsapp.tickets",
  reservations: "whatsapp.reservations",
  third_party: "whatsapp.third_party",
  otp: "whatsapp.otp",
  promotions: "whatsapp.promotions",
} as const satisfies Record<string, FeatureKey>;

/** The five ACTs (03 §2). The last two are control-panel only. */
export const WHATSAPP_ACTS = {
  viewUsage: "can-view-whatsapp-usage",
  manageSettings: "can-manage-whatsapp-settings",
  manageCampaigns: "can-manage-whatsapp-campaigns",
  managePlatform: "can-manage-whatsapp-platform",
  manageBilling: "can-manage-whatsapp-billing",
} as const satisfies Record<string, ActSlug>;

/* ---------------------------------------------------------------- errors */

/**
 * The `code` of a refused WhatsApp request (02 §11, "Errors"), with its HTTP
 * status. Errors are `{code, detail}` (+ `status_code`), raised like Hall-E's
 * `HalleAPIError`. The screens, the mocks and the tests read codes from here.
 */
export const WHATSAPP_ERROR_STATUS = {
  /** The company flag (or the sub-flag a route needs) is off. */
  FEATURE_DISABLED: 403,
  /** A campaign without an active, verified own account. */
  WHATSAPP_OWN_ACCOUNT_REQUIRED: 403,
  /** A malformed body or parameter; `detail` says what. */
  INVALID: 400,
  /** The credentials test failed; `detail` is Sparrow's message. */
  SPARROW_LOGIN_FAILED: 400,
  /** Unknown key in `params`, duplicate active purpose, or params ≠ placeholders. */
  TEMPLATE_INVALID: 400,
  /** The campaign's status does not allow this. */
  CAMPAIGN_STATE: 409,
  /** The confirmed recipient count is more than 5% off the current one. */
  ESTIMATE_CHANGED: 409,
  /** Too many OTP requests; the body carries `retry_after` (seconds). */
  OTP_THROTTLED: 429,
  /**
   * Too many offers changes (`PATCH /whatsapp/me/`, 10 an hour); the body
   * carries `retry_after` (seconds).
   */
  THROTTLED: 429,
  /**
   * The server is missing a setting: `WHATSAPP_CREDENTIAL_KEYS` (credentials
   * can't be stored) or `WHATSAPP_PUBLIC_BASE_URL` (no webhook URL can be
   * made). `detail` names which; show it.
   */
  WHATSAPP_NOT_CONFIGURED: 503,
  /** Statements: finalize before `finalize_after_days` after the month ended. */
  PERIOD_NOT_OVER: 409,
  /** Statements: usage changed since the draft; make drafts again first. */
  STATEMENT_STALE: 409,
  /** Statements: finalize on a statement that is no longer a draft. */
  NOT_A_DRAFT: 409,
  /** Statements: mark (invoiced, paid) on a statement that is not final yet. */
  NOT_FINAL: 409,
  /** Another company's idx, or gone. */
  NOT_FOUND: 404,
} as const;

export type WhatsAppErrorCode = keyof typeof WHATSAPP_ERROR_STATUS;

export interface WhatsAppErrorBody {
  code: WhatsAppErrorCode | string;
  detail?: string;
  status_code?: number;
  /** `OTP_THROTTLED`, `THROTTLED`: seconds until another try is taken. */
  retry_after?: number;
  /** `ESTIMATE_CHANGED` only: the current recipient count. */
  recipients?: number;
  /** `FEATURE_DISABLED` only: the flag that is off. */
  feature?: string;
}

/* --------------------------------------------------------- shared shapes */

export interface WhatsAppRef {
  idx: string;
  name: string;
}

/** One set of Sparrow credentials. The tokens themselves never come back. */
export interface WhatsAppAccount {
  set: boolean;
  last4: string | null;
  verified_at: string | null;
  last_error: string | null;
  last_webhook_at: string | null;
}

export interface WhatsAppTemplate {
  idx: string;
  purpose: WhatsAppPurpose;
  category: WhatsAppCategory;
  name: string;
  /** Sparrow's template ID. */
  template_id: string;
  header: WhatsAppHeader;
  /** The approved text with `{{1}}`, `{{2}}`… placeholders. */
  body_preview: string;
  /** `params[i]` is the template key that fills `{{i+1}}` (a label for campaigns). */
  params: string[];
  is_active: boolean;
  created_at: string;
}

/** POST `templates/`; PATCH takes any of the same fields. */
export interface WhatsAppTemplateBody {
  purpose: WhatsAppPurpose;
  name: string;
  template_id: string;
  header: WhatsAppHeader;
  body_preview: string;
  params: string[];
}

export type WhatsAppTemplateUpdateBody = Partial<WhatsAppTemplateBody>;

/*
 * Per-company templates on the shared number (02 §15, decision D21). A
 * shared-account template is either the platform default for its purpose
 * (`company: null`) or one company's own; that company's messages on
 * Picovico's number use it instead of the default. Control panel only:
 * SageMIN's own-number templates are unchanged.
 */

/** A shared-account template as the control panel reads it (02 §15.2). */
export interface WhatsAppPlatformTemplate extends WhatsAppTemplate {
  /** The company it is for; null = the default for every other company. */
  company: WhatsAppRef | null;
}

/**
 * POST `/whatsapp-platform/templates/`. `company_idx` absent or null = the
 * default; an unknown company is 400 INVALID; a second active one for the
 * same (company or default, purpose) is 400 TEMPLATE_INVALID.
 */
export interface WhatsAppPlatformTemplateBody extends WhatsAppTemplateBody {
  company_idx?: string | null;
}

/** PATCH `templates/<idx>/`: any of the same fields; `company_idx` moves it. */
export type WhatsAppPlatformTemplateUpdateBody =
  Partial<WhatsAppPlatformTemplateBody>;

/** `GET templates/?company=`: a company's idx, or `default` for the defaults only. */
export interface WhatsAppPlatformTemplateParams {
  company?: string;
}

/** The value of `?company=` that lists the defaults only. */
export const WHATSAPP_DEFAULT_TEMPLATES_FILTER = "default";

/** Where a company's template for a purpose comes from on Picovico's number. */
export type WhatsAppTemplateSource = "company" | "default" | "none";

/** One row of `GET companies/<idx>/templates/`: what a purpose uses now. */
export interface WhatsAppCompanyTemplateRow {
  purpose: WhatsAppPurpose;
  category: WhatsAppCategory;
  source: WhatsAppTemplateSource;
  /** Null when `source` is `none`. */
  template: WhatsAppPlatformTemplate | null;
}

/**
 * `GET /whatsapp-platform/companies/<idx>/templates/` (same ACT as
 * `config/`): one row per non-campaign purpose in `TEMPLATE_PURPOSES` order,
 * what that company's messages on Picovico's number use right now.
 */
export interface WhatsAppCompanyTemplates {
  active_mode: WhatsAppMode | null;
  templates: WhatsAppCompanyTemplateRow[];
}

/** POST `templates/<idx>/test/`: sample `values` by template key. */
export interface WhatsAppTestSendBody {
  recipient: string;
  values?: Record<string, string>;
}

/** One entry of `GET …/template-keys/`. */
export interface WhatsAppTemplateKey {
  key: string;
  label: string;
  example: string;
}

/** `GET …/template-keys/`: the vocabulary per purpose (02 §4). */
export type WhatsAppTemplateKeys = Partial<
  Record<WhatsAppPurpose, WhatsAppTemplateKey[]>
>;

export interface WhatsAppMessage {
  idx: string;
  queued_at: string;
  /** When WhatsApp reported it delivered; null until then. */
  delivered_at: string | null;
  purpose: WhatsAppMessagePurpose;
  category: WhatsAppCategory;
  /** Null for a message skipped before any account resolved (e.g. not configured). */
  mode: WhatsAppMode | null;
  /** Masked in cpanel (`98•••••678`), full in SageMIN. */
  recipient: string;
  status: WhatsAppMessageStatus;
  status_reason: string | null;
  billable: boolean;
  /** The rate snapshotted when Sparrow accepted it; `"0.00"` on an own number. */
  rate_npr: WhatsAppNpr | null;
  hall: WhatsAppRef | null;
  source_type: WhatsAppSourceType;
  source_idx: string | null;
  campaign: WhatsAppRef | null;
}

/** POST `templates/<idx>/test/` and `messages/<idx>/resend/`. */
export interface WhatsAppMessageResult {
  message: WhatsAppMessage;
}

export interface WhatsAppUsageTotals {
  messages: number;
  billable: number;
  billed_npr: WhatsAppNpr;
  own_messages: number;
  failed: number;
  unconfirmed: number;
}

export interface WhatsAppUsagePurposeRow {
  purpose: WhatsAppMessagePurpose;
  category: WhatsAppCategory;
  /** Null for messages skipped before any account resolved. */
  mode: WhatsAppMode | null;
  queued: number;
  accepted: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  rejected: number;
  skipped: number;
  unconfirmed: number;
  billable: number;
  billed_npr: WhatsAppNpr;
}

export interface WhatsAppUsageHallRow {
  /** Null for messages that belong to no hall (OTP, campaigns). */
  hall_idx: string | null;
  name: string;
  messages: number;
  billable: number;
  billed_npr: WhatsAppNpr;
  own_messages: number;
}

export interface WhatsAppUsageDayRow {
  /** `YYYY-MM-DD`, Asia/Kathmandu. */
  date: string;
  messages: number;
  billable: number;
}

/** `GET …/usage/?month=`. SageMIN adds the company's active `mode`. */
export interface WhatsAppUsage {
  month: string;
  totals: WhatsAppUsageTotals;
  by_purpose: WhatsAppUsagePurposeRow[];
  by_hall: WhatsAppUsageHallRow[];
  by_day: WhatsAppUsageDayRow[];
  mode?: WhatsAppMode | null;
}

/** The `whatsapp` sub-flags, resolved (a child is off while `whatsapp` is off). */
export interface WhatsAppSubFeatures {
  tickets: boolean;
  reservations: boolean;
  third_party: boolean;
  otp: boolean;
  promotions: boolean;
}

/** Rates per message by category, in NPR. Marketing is never billed. */
export interface WhatsAppRates {
  utility_npr: WhatsAppNpr;
  authentication_npr: WhatsAppNpr;
}

/** PUT `{api_token, partner_token}`: logged in to Sparrow first, nothing saved on failure. */
export interface WhatsAppCredentialsBody {
  api_token: string;
  partner_token: string;
}

/** `webhook_url` only on the first save (then use rotate). */
export interface WhatsAppAccountSaved {
  account: WhatsAppAccount;
  webhook_url: string | null;
}

/** POST `…/rotate-webhook/`: the new URL, shown once. */
export interface WhatsAppWebhookRotated {
  webhook_url: string;
}

/* -------------------------------------------- §11.1 control panel (cpanel) */

export interface WhatsAppWindows {
  stale_ticket_hours: number;
  unconfirmed_after_hours: number;
  finalize_after_days: number;
}

/** Delivery-report health of the shared account (02 §6). */
export interface WhatsAppHealth {
  ok: boolean;
  unconfirmed_pct_24h: number;
  last_webhook_at: string | null;
  notes: string[];
}

/** `GET /whatsapp-platform/settings/`. */
export interface WhatsAppPlatformSettings {
  shared_account: WhatsAppAccount;
  webhook_url_set: boolean;
  default_rates: WhatsAppRates;
  sending_paused: boolean;
  windows: WhatsAppWindows;
  health: WhatsAppHealth;
}

/** PATCH `settings/`. Changing `default_rates` needs the billing ACT as well. */
export interface WhatsAppPlatformSettingsBody {
  default_rates?: WhatsAppRates;
  sending_paused?: boolean;
  windows?: WhatsAppWindows;
}

export interface WhatsAppCompanyMonth {
  messages: number;
  billable: number;
  billed_npr: WhatsAppNpr;
}

/** One row of `GET /whatsapp-platform/companies/?month=`. */
export interface WhatsAppCompanyRow {
  idx: string;
  name: string;
  /** The `whatsapp` flag. */
  feature_enabled: boolean;
  features: WhatsAppSubFeatures;
  allowed_modes: WhatsAppMode[];
  active_mode: WhatsAppMode | null;
  own_account_set: boolean;
  month: WhatsAppCompanyMonth;
}

/** `GET /whatsapp-platform/companies/<idx>/config/`. */
export interface WhatsAppCompanyConfig {
  allowed_modes: WhatsAppMode[];
  active_mode: WhatsAppMode | null;
  fallback_to_shared: boolean;
  own_account: WhatsAppAccount;
  feature_enabled: boolean;
  features: WhatsAppSubFeatures;
}

/** PUT `config/`: dropping the active mode clears it. */
export interface WhatsAppCompanyConfigBody {
  allowed_modes: WhatsAppMode[];
}

/** `GET companies/<idx>/rates/`: null = the platform default. */
export interface WhatsAppCompanyRates {
  utility_npr: WhatsAppNpr | null;
  authentication_npr: WhatsAppNpr | null;
  /** What applies now: the override, else the default. */
  effective: WhatsAppRates;
}

/** PUT `rates/`: at most 2 decimals, 0 to 1000.00; null = the platform default. */
export interface WhatsAppCompanyRatesBody {
  utility_npr: WhatsAppNpr | null;
  authentication_npr: WhatsAppNpr | null;
}

/** `GET companies/<idx>/messages/` filters. */
export interface WhatsAppPlatformMessageParams {
  month?: string;
  status?: WhatsAppMessageStatus;
  purpose?: WhatsAppMessagePurpose;
  page?: number;
  page_size?: number;
}

/** One row of `GET /whatsapp-platform/statements/?period=`. */
export interface WhatsAppPlatformStatement {
  idx: string;
  company: WhatsAppRef;
  period: string;
  status: WhatsAppStatementStatus;
  /** `WAP-000001` once final; null on a draft. */
  number: string | null;
  message_count: number;
  subtotal_npr: WhatsAppNpr;
  rounding_npr: WhatsAppNpr;
  total_npr: WhatsAppNpr;
}

export interface WhatsAppStatementLine {
  kind: WhatsAppStatementLineKind;
  category: WhatsAppCategory | null;
  hall: WhatsAppRef | null;
  label: string;
  count: number;
  unit_npr: WhatsAppNpr | null;
  amount_npr: WhatsAppNpr;
}

/** `GET statements/<idx>/`. */
export interface WhatsAppPlatformStatementDetail
  extends WhatsAppPlatformStatement {
  lines: WhatsAppStatementLine[];
}

export interface WhatsAppStatementGenerateBody {
  period: string;
}

export interface WhatsAppStatementMarkBody {
  status: Extract<WhatsAppStatementStatus, "invoiced" | "paid">;
}

/* ----------------------------------------------------- §11.2 SageMIN (admin) */

/** `GET /whatsapp/status/`. */
export interface WhatsAppStatus {
  active_mode: WhatsAppMode | null;
  allowed_modes: WhatsAppMode[];
  fallback_to_shared: boolean;
  own_account: WhatsAppAccount;
  shared_ready: boolean;
  features: WhatsAppSubFeatures;
  /** `whatsapp.promotions` on and the own account verified. */
  promotions_available: boolean;
  /** The company's effective rates (override, else default). */
  rates: WhatsAppRates;
}

/**
 * PATCH `/whatsapp/settings/`. A mode outside `allowed_modes`, or `own`
 * without a verified own account, is 400 `INVALID`. The contract names no
 * answer; the mock answers the new `WhatsAppStatus`.
 */
export interface WhatsAppSettingsBody {
  active_mode?: WhatsAppMode;
  fallback_to_shared?: boolean;
}

/** DELETE `settings/own-account/`: falls to `shared` when allowed, else null. */
export interface WhatsAppOwnAccountRemoved {
  active_mode: WhatsAppMode | null;
}

/** `GET /whatsapp/messages/` filters (full numbers; `search` matches a number). */
export interface WhatsAppMessageParams extends WhatsAppPlatformMessageParams {
  hall?: string;
  search?: string;
}

/** POST `messages/<idx>/resend/`: optional new recipient; makes a new row. */
export interface WhatsAppResendBody {
  recipient?: string;
}

/** `GET /whatsapp/statements/`: final, invoiced and paid only. */
export interface WhatsAppCompanyStatement {
  idx: string;
  number: string;
  period: string;
  status: Exclude<WhatsAppStatementStatus, "draft">;
  total_npr: WhatsAppNpr;
}

/**
 * Everyone opted in, or opted-in contacts carrying any of `tags` (at most 20
 * tags of at most 64 characters each).
 */
export type WhatsAppAudience = { all: true } | { tags: string[] };

export interface WhatsAppCampaignStats {
  targeted: number;
  accepted: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  rejected: number;
  skipped: number;
}

export interface WhatsAppCampaign {
  idx: string;
  name: string;
  template: Pick<WhatsAppTemplate, "idx" | "name" | "body_preview" | "header">;
  /** One value per placeholder; `{name}` becomes the contact's name or "there". */
  params: string[];
  image_url: string | null;
  audience: WhatsAppAudience;
  status: WhatsAppCampaignStatus;
  scheduled_at: string | null;
  estimate: number | null;
  created_at: string;
  created_by: WhatsAppRef | null;
  started_at: string | null;
  finished_at: string | null;
  stats: WhatsAppCampaignStats;
}

/** POST `campaigns/` (a draft); PATCH takes any of the same while draft. */
export interface WhatsAppCampaignBody {
  name: string;
  template_idx: string;
  params: string[];
  /** The key from `campaigns/upload-image/`. */
  image?: string;
  audience: WhatsAppAudience;
}

export type WhatsAppCampaignUpdateBody = Partial<WhatsAppCampaignBody>;

/** POST `campaigns/<idx>/estimate/`. */
export interface WhatsAppCampaignEstimate {
  recipients: number;
}

/**
 * POST `campaigns/<idx>/schedule/`: `scheduled_at` null = now.
 * `confirm_recipients` is the count the person confirmed; more than 5% off
 * the current count is 409 `ESTIMATE_CHANGED`.
 */
export interface WhatsAppCampaignScheduleBody {
  scheduled_at: string | null;
  confirm_recipients: number;
}

/** POST `campaigns/upload-image/` (multipart `file`, jpeg/png ≤ 5 MB). */
export interface WhatsAppImageUpload {
  image: string;
  preview_url: string;
}

export interface WhatsAppContact {
  idx: string;
  number: string;
  name: string;
  opted_in: boolean;
  opt_in_source: WhatsAppOptInSource | null;
  opted_in_at: string | null;
  opted_out_at: string | null;
  opt_out_source: WhatsAppOptOutSource | null;
  tags: string[];
}

/** `GET contacts/` filters. */
export interface WhatsAppContactParams {
  search?: string;
  opted_in?: boolean;
  tag?: string;
  page?: number;
  page_size?: number;
}

/** PATCH `contacts/<idx>/`: admins may only opt a contact out. */
export interface WhatsAppContactUpdateBody {
  opted_in?: false;
  tags?: string[];
}

/** POST `contacts/import/` (multipart `file` + `attest: true`). */
export interface WhatsAppContactImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: { row: number; reason: string }[];
}

export interface WhatsAppContactTag {
  tag: string;
  count: number;
}

/* -------------------------------------------------- §11.3 customer (web, app) */

/** `GET /whatsapp/me/` (needs the `whatsapp` flag; 403 otherwise). */
export interface WhatsAppMe {
  /** `whatsapp.tickets` on and a ticket message can be sent now. */
  tickets: boolean;
  /** `whatsapp.tickets` on and a food-order message can be sent now. */
  food: boolean;
  otp: boolean;
  offers: {
    /**
     * Promotions available (`whatsapp.promotions` on, the company's own number
     * verified) **and** the customer has a verified mobile.
     */
    available: boolean;
    opted_in: boolean;
    /** The customer's verified mobile, where offers go; null without one. */
    number: string | null;
  };
}

/**
 * PATCH `/whatsapp/me/`. Offers only ever go to the verified mobile: a
 * `number` that differs is 400 `INVALID`, opting in while `offers.available`
 * is false is 400 `INVALID`, and more than 10 changes an hour are 429
 * `THROTTLED` with `retry_after`. The web sends `{offers_opt_in}` only.
 */
export interface WhatsAppMeUpdateBody {
  offers_opt_in: boolean;
  number?: string;
}

/**
 * The new optional fields on POST `/cinema/booking/payment-initiate/` and
 * `/cinema/food/payment/checkout/`. Ignored while `whatsapp.tickets` is off;
 * an invalid number is a 400 in the serializer's usual shape,
 * `{error: {whatsapp_to: ["Enter a valid mobile number."]}, status_code: 400}`.
 * `whatsapp_offers: true` opts in the customer's **verified mobile** (not
 * `whatsapp_to`) when offers are available; false or absent changes nothing.
 */
export interface WhatsAppCheckoutFields {
  whatsapp_to?: string;
  whatsapp_offers?: boolean;
}

/** POST `/member/verify/otp/`: the new optional body. */
export interface WhatsAppOtpRequestBody {
  channel?: WhatsAppOtpChannel;
}

/**
 * What POST `/member/verify/otp/` answers inside `data`. `channel` and
 * `fallback_channels` are new (02 §10); an older server leaves them out.
 */
export interface WhatsAppOtpSent {
  message?: string;
  verification_id: string;
  channel?: WhatsAppOtpChannel;
  /** `["sms"]` when SMS is possible, else `[]`. */
  fallback_channels?: WhatsAppOtpChannel[];
}

/**
 * Any other refusal of POST `/member/verify/otp/` (amphitheater's
 * `api_error_response`): the list holds texts or `{message}` objects, e.g.
 * 502 `{error: {message: [{message: "Could not send the verification code.
 * Please try again."}], status_code: 502}}` when WhatsApp failed and SMS is
 * not possible. `parseServerError` (`@picovico/utils`) reads both.
 */
export interface WhatsAppOtpRefused {
  error: {
    message: (string | { message: string })[];
    status_code: number;
  };
}

/** 429 from POST `/member/verify/otp/`. */
export interface WhatsAppOtpThrottled extends WhatsAppErrorBody {
  code: "OTP_THROTTLED";
  retry_after: number;
}

/* ------------------------------------------------------ response envelopes */

export type WhatsAppPlatformSettingsSuccessResponse =
  BaseSuccessResponse<WhatsAppPlatformSettings>;
export type WhatsAppAccountSavedSuccessResponse =
  BaseSuccessResponse<WhatsAppAccountSaved>;
export type WhatsAppWebhookRotatedSuccessResponse =
  BaseSuccessResponse<WhatsAppWebhookRotated>;
export type WhatsAppTemplateListSuccessResponse = BaseSuccessResponse<
  WhatsAppTemplate[]
>;
export type WhatsAppTemplateSuccessResponse =
  BaseSuccessResponse<WhatsAppTemplate>;
export type WhatsAppPlatformTemplateListSuccessResponse = BaseSuccessResponse<
  WhatsAppPlatformTemplate[]
>;
export type WhatsAppPlatformTemplateSuccessResponse =
  BaseSuccessResponse<WhatsAppPlatformTemplate>;
export type WhatsAppCompanyTemplatesSuccessResponse =
  BaseSuccessResponse<WhatsAppCompanyTemplates>;
export type WhatsAppTemplateKeysSuccessResponse =
  BaseSuccessResponse<WhatsAppTemplateKeys>;
export type WhatsAppMessageResultSuccessResponse =
  BaseSuccessResponse<WhatsAppMessageResult>;
export type WhatsAppMessagePageSuccessResponse = BaseSuccessResponse<
  PaginatedData<WhatsAppMessage>
>;
export type WhatsAppUsageSuccessResponse = BaseSuccessResponse<WhatsAppUsage>;
export type WhatsAppCompanyListSuccessResponse = BaseSuccessResponse<
  WhatsAppCompanyRow[]
>;
export type WhatsAppCompanyConfigSuccessResponse =
  BaseSuccessResponse<WhatsAppCompanyConfig>;
export type WhatsAppCompanyRatesSuccessResponse =
  BaseSuccessResponse<WhatsAppCompanyRates>;
export type WhatsAppPlatformStatementListSuccessResponse = BaseSuccessResponse<
  WhatsAppPlatformStatement[]
>;
export type WhatsAppPlatformStatementSuccessResponse =
  BaseSuccessResponse<WhatsAppPlatformStatement>;
export type WhatsAppPlatformStatementDetailSuccessResponse =
  BaseSuccessResponse<WhatsAppPlatformStatementDetail>;
export type WhatsAppStatusSuccessResponse = BaseSuccessResponse<WhatsAppStatus>;
export type WhatsAppOwnAccountRemovedSuccessResponse =
  BaseSuccessResponse<WhatsAppOwnAccountRemoved>;
export type WhatsAppCompanyStatementListSuccessResponse = BaseSuccessResponse<
  WhatsAppCompanyStatement[]
>;
export type WhatsAppCampaignPageSuccessResponse = BaseSuccessResponse<
  PaginatedData<WhatsAppCampaign>
>;
export type WhatsAppCampaignSuccessResponse =
  BaseSuccessResponse<WhatsAppCampaign>;
export type WhatsAppCampaignEstimateSuccessResponse =
  BaseSuccessResponse<WhatsAppCampaignEstimate>;
export type WhatsAppImageUploadSuccessResponse =
  BaseSuccessResponse<WhatsAppImageUpload>;
export type WhatsAppContactPageSuccessResponse = BaseSuccessResponse<
  PaginatedData<WhatsAppContact>
>;
export type WhatsAppContactSuccessResponse =
  BaseSuccessResponse<WhatsAppContact>;
export type WhatsAppContactImportSuccessResponse =
  BaseSuccessResponse<WhatsAppContactImportResult>;
export type WhatsAppContactTagListSuccessResponse = BaseSuccessResponse<
  WhatsAppContactTag[]
>;
export type WhatsAppMeSuccessResponse = BaseSuccessResponse<WhatsAppMe>;
export type WhatsAppOtpSentSuccessResponse =
  BaseSuccessResponse<WhatsAppOtpSent>;
export * from "./delivery";
